import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { fieldsValidators } from '@shared/constants/field-name-validators';
import { GridDrawerComponent, SecurityType } from '../../models';

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gstar-upsert-security-type',
  templateUrl: './upsert-security-type.component.html',
  styleUrl: './upsert-security-type.component.scss',
})
export class UpsertSecurityTypeComponent
  extends GridDrawerComponent<SecurityType>
  implements OnChanges, OnInit
{
  private fb = inject(FormBuilder);

  public readonly maxNameLength = 255;
  enableUpdate: boolean = false;
  parentName: WritableSignal<string> = signal<string>('');
  isParent: WritableSignal<boolean> = signal<boolean>(false);

  destroyRef = inject(DestroyRef);

  public form: FormGroup = this.fb.group({
    name: [
      '',
      [
        Validators.required,
        Validators.maxLength(this.maxNameLength),
        Validators.pattern(fieldsValidators.letterRequired),
      ],
    ],
    isActive: true,
    parentSecurityTypeId: [null, [Validators.required]],
  });

  highlightText!: string;

  get nameField(): AbstractControl<string> {
    return this.form.get('name')!;
  }

  get parentField(): AbstractControl<string> {
    return this.form.get('parentSecurityTypeId')!;
  }

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((values: SecurityType) => {
        if (this.isEdit) this.checkChanges(values);
        this.modelChange.emit({
          model: {
            ...this.model,
            ...values,
          },
          prevModel: this.model!,
          isValid:
            this.isEdit && this.form.valid
              ? this.enableUpdate
              : this.form.valid,
        });
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const modelChanges = changes['model'];
    if (modelChanges?.currentValue?.isParent) {
      this.form.get('isActive')?.disable();
      this.isParent.set(true);
    } else if (this.isEdit) {
      this.parentName.set(modelChanges.currentValue?.parentName);
    }
    if (modelChanges) {
      this.form.setValue({
        name: modelChanges.currentValue?.name,
        isActive: !!modelChanges.currentValue?.isActive,
        parentSecurityTypeId:
          modelChanges.currentValue?.parentSecurityTypeId ?? 0,
      });
    }
  }

  private checkChanges(values: SecurityType): void {
    if (values && this.model) {
      this.enableUpdate =
        values.name !== this.model.name ||
        values.isActive !== this.model.isActive ||
        values.parentSecurityTypeId !== this.model.parentSecurityTypeId;
    }
  }

  selectSecurityType(securityType: SecurityType | null | undefined): void {
    this.form.setValue({
      name: this.form.get('name')?.value,
      isActive: this.form.get('isActive')?.value,
      parentSecurityTypeId: securityType?.securityTypeId,
    });
  }

  clearSecurityType(): void {
    this.form.setValue({
      name: this.form.get('name')?.value,
      isActive: this.form.get('isActive')?.value,
      parentSecurityTypeId: null,
    });
  }
}
